// Cookie-consent persistence layer.
//
// State shape (single localStorage entry — easier to reason about than
// per-category keys, and the whole record can be invalidated atomically
// by bumping `version` when categories change):
//
//   {
//     necessary:       true,   // locked — strictly required cookies/storage
//     analytics:       false,  // GA4 + Sentry (default opt-out, GDPR Art. 7)
//     marketing:       false,  // ad campaigns, Meta Pixel etc.
//     personalization: false,  // personalized content / recommendations
//     decidedAt: <iso>,        // null until the user makes any decision;
//                              // null = banner visible.
//     version: 2               // bump when adding a category that needs
//                              // explicit re-consent, or when scope changes.
//   }
//
// Pure functions only. The ConsentContext wraps these for React state.

const STORAGE_KEY = 'petzy_cookie_consent';
// v2 split the previous single "ad_*" axis into separate Marketing and
// Personalization categories and added Sentry to the Analytics scope, so
// any v1 record needs explicit re-consent.
//
// v3 (2026-05-25) adds Meta Pixel as a named processor in the Marketing
// category. The pixel-load is strict-refuse-gated client-side (see the
// inline IIFE in index.html), but GDPR Art. 7(2)+(4) requires *specific
// + informed* consent — a user who accepted the previous generic
// "Marketing" copy didn't know data would flow to Meta in the US under
// SCC+DPF. Bumping forces the banner to re-show so the user can decide
// against the updated cookies-policy text (PR #903 added the Meta Pixel
// paragraph there). This matches the auto-re-consent behavior Cookiebot
// and OneTrust trigger on detecting a new named processor in an
// existing category.
//
// IMPORTANT — keep in sync with the CONSENT_VERSION literal in
// frontend/index.html (inline bootstrap inside <head>). The inline
// script reads `petzy_cookie_consent` from localStorage before React
// mounts and uses its own copy of this constant to decide whether to
// load gtag.js for a returning visitor. Bumping the React-side
// version without bumping the inline copy lets a stale record sneak
// past the strict-refuse gate on first paint, while React-side
// getConsent() correctly resets decidedAt and re-prompts via the
// banner. The two have to move together.
export const CONSENT_VERSION = 3;

// Default state for a user who has never decided. The banner watches
// `decidedAt` — null means show, anything else means hide.
export const DEFAULT_CONSENT = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
  personalization: false,
  decidedAt: null,
  version: CONSENT_VERSION,
});

// Browser DNT signal. Soft default — when true, analytics starts
// unchecked in the settings modal. The user can still manually opt in,
// we just don't preselect it. Treated as advisory, not regulation.
// MDN: navigator.doNotTrack returns '1' when DNT is on, '0' or 'unspecified'/null otherwise.
export function browserDoNotTrack() {
  try {
    return navigator.doNotTrack === '1' || window.doNotTrack === '1';
  } catch {
    return false;
  }
}

// Read the saved consent (if any). Returns DEFAULT_CONSENT for first-time
// visitors, for users on a stale version (we re-prompt by surfacing the
// banner via decidedAt: null), and for any localStorage failure
// (incognito, quota, disabled).
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CONSENT };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_CONSENT };
    // Stale category set — drop the saved decision so the banner
    // reappears with the new options. Keep the booleans we still
    // recognise as a starting point in the settings modal so the user
    // doesn't have to re-pick everything.
    if (parsed.version !== CONSENT_VERSION) {
      return {
        ...DEFAULT_CONSENT,
        analytics: !!parsed.analytics,
        marketing: !!parsed.marketing,
        personalization: !!parsed.personalization,
      };
    }
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      personalization: !!parsed.personalization,
      decidedAt: parsed.decidedAt || null,
      version: CONSENT_VERSION,
    };
  } catch {
    return { ...DEFAULT_CONSENT };
  }
}

// Write the consent record. Always stamps `decidedAt` and `version` so
// the caller can't forget to invalidate the banner state.
export function saveConsent({ analytics, marketing, personalization }) {
  const next = {
    necessary: true,
    analytics: !!analytics,
    marketing: !!marketing,
    personalization: !!personalization,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Soft-fail — incognito or storage-disabled browser. The current
    // session keeps the consent in React state via the context; we
    // just can't persist it. Banner will show again on next visit.
  }
  return next;
}

// Restore a previously-decided record coming from the NATIVE mirror
// (@capacitor/preferences survives WebView storage eviction under
// memory pressure; localStorage alone does not). Unlike saveConsent,
// the original decidedAt is PRESERVED — this is a recovery of the
// user's earlier decision, not a new one. Returns the normalised state
// on success, or null when the record is missing/stale (wrong version
// or never decided) so the caller falls back to showing the banner.
export function restoreConsent(record) {
  if (!record || typeof record !== 'object') return null;
  if (record.version !== CONSENT_VERSION || !record.decidedAt) return null;
  const next = {
    necessary: true,
    analytics: !!record.analytics,
    marketing: !!record.marketing,
    personalization: !!record.personalization,
    decidedAt: record.decidedAt,
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Same soft-fail as saveConsent — React state still gets the value.
  }
  return next;
}
